import { areStringArraysEqual } from "@/common/helpers/are-string-arrays-equal";
import { sortByLabel } from "@/common/helpers/sort-by-label";
import { cn } from "@/common/helpers/utils";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { InputVariant, inputVariants } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { type VariantProps, cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { CheckIcon, ChevronDown, XCircle, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const CHIP_BASE_PADDING = 20;
const CHIP_BORDER_WIDTH = 2;
const CHIP_ICON_WIDTH = 16;
const CHIP_ICON_GAP = 8;
const CHIP_BUFFER = 6;

const multiSelectVariants = cva("transition-colors", {
  variants: {
    variant: {
      default: "border-foreground/10 text-foreground bg-card data-[clickable]:hover:bg-card/80",
      secondary: "border-foreground/10 bg-secondary text-secondary-foreground data-[clickable]:hover:bg-secondary/80",
      success: "border-transparent bg-success text-success-foreground data-[clickable]:hover:bg-success/80",
      warning: "border-transparent bg-warning text-warning-foreground data-[clickable]:hover:bg-warning/80",
      error: "border-transparent bg-error text-error-foreground data-[clickable]:hover:bg-error/80",
      inverted: "inverted",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface OptionMeta {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof multiSelectVariants> {
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  onValueChange: (value: string[]) => void;
  defaultValue?: string[];
  value?: string[];
  placeholder?: string;
  maxCount?: number | "auto";
  modalPopover?: boolean;
  className?: string;
  inputVariant?: InputVariant;
  ref?: React.Ref<HTMLButtonElement>;
}

export const MultiSelect = ({
  options,
  onValueChange,
  variant,
  defaultValue = [],
  placeholder = "Select options",
  maxCount = 3,
  modalPopover = false,
  className,
  inputVariant,
  ref,
  ...props
}: MultiSelectProps) => {
  const { t } = useTranslation("common");

  const [selectedValuesState, setSelectedValuesState] = useState<string[]>(defaultValue);
  const [prevDefaultValue, setPrevDefaultValue] = useState<string[]>(defaultValue);
  if (!areStringArraysEqual(prevDefaultValue, defaultValue)) {
    setPrevDefaultValue(defaultValue);
    setSelectedValuesState(defaultValue);
  }
  const selectedValues = props.value !== undefined ? props.value : selectedValuesState;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsingAnimation, setIsCollapsingAnimation] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(() => (maxCount === "auto" ? 1 : maxCount));
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null): void => {
      triggerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
        return;
      }
      if (ref) {
        (ref as React.RefObject<HTMLButtonElement | null>).current = node;
      }
    },
    [ref],
  );

  const optionMap = useMemo<Map<string, OptionMeta>>(
    () => new Map(options.map((option) => [option.value, { label: option.label, icon: option.icon }])),
    [options],
  );

  const selectedValueSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const sortedSelectedValues = useMemo(
    () => sortByLabel(selectedValues, (value) => optionMap.get(value)?.label ?? value),
    [selectedValues, optionMap],
  );

  const getTextWidth = useCallback((text: string, font: string): number => {
    const canvas = canvasRef.current ?? document.createElement("canvas");
    canvasRef.current = canvas;
    const context = canvas.getContext("2d");
    if (!context) {
      return text.length * 8;
    }
    context.font = font;
    return context.measureText(text).width;
  }, []);

  const getFontString = useCallback((element: HTMLButtonElement): string => {
    const style = window.getComputedStyle(element);
    const fontFamily = style.fontFamily || "sans-serif";
    const fontWeight = style.fontWeight || "400";
    const fontSize = "0.75rem";
    return `${fontWeight} ${fontSize} ${fontFamily}`;
  }, []);

  const getChipWidths = useCallback(
    (values: string[], font: string): number[] =>
      values
        .map((value) => optionMap.get(value))
        .filter((option): option is OptionMeta => option !== undefined)
        .map((option) => {
          const labelWidth = getTextWidth(option.label, font);
          const leftIconWidth = option.icon ? CHIP_ICON_WIDTH + CHIP_ICON_GAP : 0;
          return Math.ceil(
            labelWidth +
              CHIP_BASE_PADDING +
              CHIP_BORDER_WIDTH +
              leftIconWidth +
              CHIP_ICON_WIDTH +
              CHIP_ICON_GAP +
              CHIP_BUFFER,
          );
        }),
    [getTextWidth, optionMap],
  );

  const getMoreChipWidth = useCallback(
    (hiddenCount: number, font: string): number => {
      const label = `+ ${Math.max(0, hiddenCount)} ${t("More")}`;
      const labelWidth = getTextWidth(label, font);
      return Math.ceil(labelWidth + CHIP_BASE_PADDING + CHIP_BORDER_WIDTH + CHIP_BUFFER);
    },
    [getTextWidth, t],
  );

  const fitsInRow = useCallback((rowWidth: number, widths: number[], gap: number): boolean => {
    let used = 0;
    for (let index = 0; index < widths.length; index += 1) {
      const width = widths[index];
      const add = (used > 0 ? gap : 0) + width;
      if (used + add > rowWidth) {
        return false;
      }
      used += add;
    }
    return true;
  }, []);

  const findMaxVisibleCount = useCallback(
    (rowWidth: number, chipWidths: number[], font: string, gap: number): number => {
      let low = 0;
      let high = chipWidths.length;

      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        const hiddenCount = chipWidths.length - mid;

        const widthsToPlace =
          hiddenCount > 0
            ? [...chipWidths.slice(0, mid), getMoreChipWidth(hiddenCount, font)]
            : chipWidths.slice(0, mid);

        if (fitsInRow(rowWidth, widthsToPlace, gap)) {
          low = mid;
        } else {
          high = mid - 1;
        }
      }

      return low;
    },
    [fitsInRow, getMoreChipWidth],
  );

  const resolvedMaxCount =
    isExpanded || isCollapsingAnimation ? selectedValues.length : maxCount === "auto" ? visibleCount : maxCount;

  const handleExpand = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setIsExpanded(true);
  };

  const handleCollapse = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setIsCollapsingAnimation(true);
    setIsExpanded(false);
  };

  const collapsedMaxCount = maxCount === "auto" ? visibleCount : maxCount;

  if (isExpanded && selectedValues.length <= collapsedMaxCount) {
    setIsExpanded(false);
  }

  useEffect(() => {
    if (maxCount !== "auto") {
      return;
    }

    const triggerElement = triggerRef.current;
    if (!triggerElement) {
      return;
    }

    const updateVisibleCount = (): void => {
      const triggerWidth = triggerElement.clientWidth;
      const controlsWidth = controlsRef.current?.offsetWidth ?? 0;
      const rowWidth = Math.max(0, triggerWidth - controlsWidth);
      const font = getFontString(triggerElement);
      const chipWidths = getChipWidths(sortedSelectedValues, font);
      const count = findMaxVisibleCount(rowWidth, chipWidths, font, 4);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- DOM layout measurement; runs synchronously on mount/deps change to avoid a frame with the wrong chip count
      setVisibleCount(count);
    };

    updateVisibleCount();

    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(triggerElement);

    return () => observer.disconnect();
  }, [findMaxVisibleCount, getChipWidths, getFontString, maxCount, sortedSelectedValues]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      setIsPopoverOpen(true);
    } else if (event.key === "Backspace" && !event.currentTarget.value) {
      const nextSelected = [...selectedValues];
      nextSelected.pop();
      if (props.value === undefined) {
        setSelectedValuesState(nextSelected);
      }
      onValueChange(nextSelected);
    }
  };

  const toggleOption = (option: string): void => {
    const nextSelected = selectedValues.includes(option)
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option];

    if (props.value === undefined) {
      setSelectedValuesState(nextSelected);
    }
    onValueChange(nextSelected);
  };

  const handleClear = (): void => {
    if (props.value === undefined) {
      setSelectedValuesState([]);
    }
    onValueChange([]);
  };

  const handleTogglePopover = (): void => {
    setIsPopoverOpen((prev) => !prev);
  };

  const toggleAll = (): void => {
    if (selectedValues.length === options.length) {
      handleClear();
    } else {
      const allValues = options.map((option) => option.value);
      if (props.value === undefined) {
        setSelectedValuesState(allValues);
      }
      onValueChange(allValues);
    }
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
      <PopoverTrigger asChild>
        <Button
          ref={setTriggerRef}
          {...props}
          onClick={handleTogglePopover}
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          className={cn(
            inputVariants({ variant: inputVariant }),
            "flex h-auto min-h-9 min-w-0 items-center justify-between px-2 py-1 text-sm",
            "hover:bg-accent hover:text-accent-foreground transition-colors",
            "[&_svg]:pointer-events-auto",
            className,
          )}
        >
          {selectedValues.length > 0 ? (
            <div className="flex w-full items-center justify-between">
              <div className="flex min-w-0 flex-1 items-center">
                <motion.div
                  style={{ overflow: "hidden" }}
                  animate={{ height: isExpanded ? "auto" : "1.5rem" }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  className="w-full"
                  onAnimationComplete={() => setIsCollapsingAnimation(false)}
                >
                  <div className="flex flex-wrap content-start items-start gap-1 py-px">
                    {sortedSelectedValues.slice(0, resolvedMaxCount).map((value) => {
                      const option = optionMap.get(value);
                      const IconComponent = option?.icon;

                      return (
                        <Badge key={value} className={cn("max-w-full min-w-0", multiSelectVariants({ variant }))}>
                          {IconComponent && <IconComponent className="mr-2 h-4 w-4" />}
                          <span className="min-w-0 truncate">{option?.label}</span>
                          <XCircle
                            className="ml-2 h-4 w-4 cursor-pointer"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOption(value);
                            }}
                          />
                        </Badge>
                      );
                    })}
                    {!isExpanded && selectedValues.length > resolvedMaxCount && (
                      <Badge
                        className={cn("max-w-full min-w-0 cursor-pointer", multiSelectVariants({ variant }))}
                        onClick={handleExpand}
                      >
                        <span className="min-w-0 truncate">{`+ ${selectedValues.length - resolvedMaxCount} ${t("More")}`}</span>
                      </Badge>
                    )}
                    {isExpanded && (
                      <Badge
                        className={cn("max-w-full min-w-0 cursor-pointer", multiSelectVariants({ variant }))}
                        onClick={handleCollapse}
                      >
                        <ChevronDown className="mr-1 h-3 w-3 rotate-180" />
                        <span className="min-w-0 truncate">{t("Collapse")}</span>
                      </Badge>
                    )}
                  </div>
                </motion.div>
              </div>
              <div ref={controlsRef} className="flex shrink-0 items-center justify-between self-center">
                <XIcon
                  className="text-muted-foreground mx-2 h-4 cursor-pointer"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClear();
                  }}
                />
                <Separator orientation="vertical" className="flex h-full min-h-6" />
                <ChevronDown
                  className={cn(
                    "text-muted-foreground mx-2 h-4 cursor-pointer transition-transform duration-200",
                    isPopoverOpen && "rotate-180",
                  )}
                />
              </div>
            </div>
          ) : (
            <div className="mx-auto flex w-full items-center justify-between">
              <span className="text-muted-foreground mx-1 font-normal">{placeholder}</span>
              <ChevronDown
                className={cn(
                  "text-muted-foreground mx-2 h-4 cursor-pointer transition-transform duration-200",
                  isPopoverOpen && "rotate-180",
                )}
              />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" onEscapeKeyDown={() => setIsPopoverOpen(false)}>
        <Command
          filter={(value, search, keywords) => {
            const searchLower = search.toLowerCase();
            const matchTarget = keywords?.join(" ").toLowerCase() ?? value.toLowerCase();
            return matchTarget.includes(searchLower) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={t("Search")} onKeyDown={handleInputKeyDown} />
          <CommandList>
            <CommandEmpty>{t("NoResultsFound")}</CommandEmpty>
            <CommandGroup>
              <CommandItem key="all" onSelect={toggleAll} className="cursor-pointer">
                <div
                  className={cn(
                    "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-xs border",
                    selectedValues.length === options.length
                      ? "bg-primary text-primary-foreground"
                      : "opacity-50 [&_svg]:invisible",
                  )}
                >
                  <CheckIcon className="h-4 w-4" />
                </div>
                <span>{t("SelectAll")}</span>
              </CommandItem>
              {options.map((option) => {
                const isSelected = selectedValueSet.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label, String(option.value)]}
                    onSelect={() => toggleOption(option.value)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-xs border",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    {option.icon && <option.icon className="text-muted-foreground mr-2 h-4 w-4" />}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <div className="flex items-center justify-between">
                {selectedValues.length > 0 && (
                  <>
                    <CommandItem onSelect={handleClear} className="flex-1 cursor-pointer justify-center">
                      {t("Reset")}
                    </CommandItem>
                    <Separator orientation="vertical" className="flex h-full min-h-6" />
                  </>
                )}
                <CommandItem
                  onSelect={() => setIsPopoverOpen(false)}
                  className="max-w-full flex-1 cursor-pointer justify-center"
                >
                  {t("Close")}
                </CommandItem>
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

MultiSelect.displayName = "MultiSelect";
